export type TrackState =
  | 'DRAFTING_SPEC'
  | 'SPEC_REVIEW'
  | 'AWAITING_PREREQS'
  | 'QUEUED'
  | 'BUILDING'
  | 'VERIFYING'
  | 'PR_OPEN'
  | 'MERGED'
  | 'DONE'
  | 'BLOCKED_HUMAN'
  | 'FAILED_BUILD'
  | 'FAILED_SCOPE'
  | 'CANCELLED';

export interface Prereq {
  id: string;
  title: string;
  steps: string[];
  env_vars?: string[];
  paste_into?: string;
}

export interface FileModify {
  path: string;
  why: string;
}

export interface Spec {
  title: string;
  one_line: string;
  manifest: {
    files_create: string[];
    files_modify: FileModify[];
    tables_create: string[];
  };
  will_not_touch: string[];
  prereqs: Prereq[];
  cc_prompt: string;
  success_criteria: string[];
  rollback: { git_tag: string; side_effects: string };
  risk_level: 'low' | 'medium' | 'high';
  estimated_build_minutes?: number;
  target_repo?: 'sidwellgrav/biltyn' | 'sidwellgrav/conductor';
}

export interface Track {
  id: string;
  title: string;
  one_line: string;
  state: TrackState;
  spec: Spec;
  blocked_by: string[];
  rollback_tag: string | null;
  pr_url: string | null;
  pr_number: number | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  built_at: string | null;
  merged_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  deploy_status: string | null;
  deploy_checked_at: string | null;
  last_session_outcome: string | null;
  last_session_summary: string | null;
  needs_input_at: string | null;
  foreman_state: Record<string, boolean> | null;
  session_url: string | null;
  module: string | null;
}

export interface Lock {
  file_path: string;
  track_id: string;
  acquired_at: string;
}

export interface PrRegistryRow {
  repo: string;
  pr_number: number;
  title: string;
  branch: string;
  opened_at: string;
  author: string;
  track_id: string | null;
  origin: string;
  module: string | null;
  verdicts: { review_verdict: string | null; ciso_verdict: string | null } | null;
  age_hours: number;
  status_hint: string;
}

export interface ActivityEntry {
  id: string;
  type: 'cancelled' | 'blocked' | 'unblocked' | 'foreman' | 'requeued' | 'shipped';
  timestamp: string;
  track_id: string | null;
  module: string | null;
  label: string;
  outcome: string | null;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const message = body?.error ?? `request failed: ${res.status}`;
    throw new ApiError(res.status, message, body?.code, body?.details);
  }
  return body as T;
}

function safeJson(text: string): { error?: string; code?: string; details?: unknown } & Record<string, unknown> {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

export const api = {
  listTracks(state?: TrackState): Promise<{ tracks: Track[] }> {
    const qs = state ? `?state=${encodeURIComponent(state)}` : '';
    return request(`/api/tracks${qs}`);
  },
  getTrack(id: string): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}`);
  },
  createTrack(spec: unknown): Promise<{ track: Track; blocked_by_preview: string[] }> {
    return request(`/api/tracks`, { method: 'POST', body: JSON.stringify(spec) });
  },
  approve(id: string): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
  },
  prereqsDone(id: string): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}/prereqs-done`, { method: 'POST', body: JSON.stringify({}) });
  },
  cancel(id: string, reason?: string): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  revise(id: string, newSpec?: unknown): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}/revise`, {
      method: 'POST',
      body: JSON.stringify(newSpec ?? {}),
    });
  },
  setState(id: string, to_state: TrackState, reason?: string): Promise<{ track: Track }> {
    return request(`/api/tracks/${id}/state`, {
      method: 'POST',
      body: JSON.stringify({ to_state, reason }),
    });
  },
  listLocks(): Promise<{ locks: Lock[] }> {
    return request(`/api/locks`);
  },
  offLimits(): Promise<{ patterns: string[] }> {
    return request(`/api/off-limits`);
  },
  getPrRegistry(): Promise<{ fetched_at: string | null; rows: PrRegistryRow[] }> {
    return request(`/api/pr-registry`);
  },
  getActivity(): Promise<{ entries: ActivityEntry[] }> {
    return request(`/api/activity`);
  },
};
